import React from 'react';

import { useMutation, useQuery, useQueryClient } from '@lumapps/base-api/react-query';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL } from '@lumapps/translations';
import { getLanguageHeader } from '@lumapps/translations/ducks/selectors';
import { GetBookmarkResponse, GetUserBookmarkParams } from '@lumapps/user-bookmarks/api';
import { userIdSelector } from '@lumapps/user/ducks/selectors';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { createSavedItem, deleteSavedItem, getSavedItem, savedItemQueryKeys } from '../api';
import { USER_SAVED_ITEMS } from '../keys';
import { ToggleSaveVariables } from '../types';

export interface UseSaveButtonParams extends Pick<GetUserBookmarkParams, 'resourceId' | 'resourceType' | 'priority'> {
    fetchOnMount?: boolean;
}

/**
 * Hook that will fetch the saved status of the given object and return the props to manage the saving.
 *
 * The saved status is fetched through React Query keyed on the target resource, so navigating to another
 * resource (a new `resourceId`) automatically refetches instead of keeping the previous resource's status.
 */
export const useSaveButton = ({ resourceId, resourceType, fetchOnMount, priority }: UseSaveButtonParams) => {
    const currentUserId = useSelector(userIdSelector) || '';
    const acceptLanguageHeader = useSelector(getLanguageHeader);

    const { error: notificationError } = useNotification();
    const queryClient = useQueryClient();

    const getSavedItemParams = {
        userId: currentUserId,
        resourceId,
        resourceType,
        priority,
        acceptLanguageHeader,
    };
    const queryKey = savedItemQueryKeys.detail(getSavedItemParams);

    const query = useQuery<GetBookmarkResponse>({
        // `queryKey` already captures every `getSavedItemParams` field the queryFn depends on; the rule
        // just can't see through the `savedItemQueryKeys.detail(...)` factory call.
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
        queryFn: () => getSavedItem(getSavedItemParams).then((response) => response.data),
        enabled: Boolean(fetchOnMount) && Boolean(resourceId),
        onError: () => {
            notificationError({ translate: USER_SAVED_ITEMS.SAVE_UNAVAILABLE });
        },
    });

    const savedItemId = query.data?.items[0]?.id;

    const saveMutation = useMutation({
        mutationFn: ({ userId, resourceId: id, resourceType: type, savedItemId: itemId }: ToggleSaveVariables) =>
            itemId === undefined
                ? createSavedItem({ userId, resourceId: id, resourceType: type })
                : deleteSavedItem({ userId, itemId }),
        onSuccess: (response, { queryKey: targetQueryKey }) => {
            // Update the cache from the queryKey in the variables (not the closure) so the result lands
            // on the resource the toggle was fired for, even after navigating away.
            if (response.status === 201 || response.status === 204) {
                queryClient.setQueryData<GetBookmarkResponse>(targetQueryKey, (previous) => ({
                    cursor: previous?.cursor ?? '',
                    more: previous?.more ?? false,
                    items: response.status === 201 ? [response.data] : [],
                }));
            }
        },
        onError: () => {
            notificationError({ translate: GLOBAL.ACTION_NOT_COMPLETED_TRY_AGAIN });
        },
    });

    // Rely on `isInitialLoading` so a disabled/not-yet-fetched button reads as `idle`, not `loading`.
    let status: BaseLoadingStatus;
    if (query.isError) {
        status = BaseLoadingStatus.error;
    } else if (query.isInitialLoading) {
        status = BaseLoadingStatus.loading;
    } else {
        status = BaseLoadingStatus.idle;
    }

    const isDisabled = saveMutation.isLoading || status === BaseLoadingStatus.error;

    const { refetch } = query;
    const fetchSavedItem = React.useCallback(() => {
        refetch();
    }, [refetch]);

    const { mutate } = saveMutation;
    const onClick = React.useCallback(() => {
        // Wait for the status to be known: otherwise create-vs-delete is picked from an undefined `savedItemId`.
        if (isDisabled || !query.isSuccess) {
            return;
        }
        mutate({ queryKey, userId: currentUserId, resourceId, resourceType, savedItemId });
    }, [isDisabled, query.isSuccess, mutate, queryKey, currentUserId, resourceId, resourceType, savedItemId]);

    return {
        fetchSavedItem,
        status,
        isSaved: savedItemId !== undefined,
        onClick,
        isDisabled,
    };
};
